import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
  FilterNonIdeal,
} from "../../../common";
import AppTableFilters from "../../../common/AppTableFilters";
import { AppPagination, UBButton } from "../../../../Libraries";
import { DoubleArrowIcon } from "../../../../assets/icons/icon.jsx";
import { LanguageContext } from "../../../../context/LanguageContext";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { buildFiltersConfig } from "../../../../utils";
import moment from "moment";
import { formatNum } from "../../../../oscar-pos-core/constants";
import useTransactionListing, { initialListingFilters } from "../../../../hooks/useTransactionListing";
import useBillingCustomers from "../../../../hooks/useBillingCustomers";
import useBillingConsumers from "../../../../hooks/useBillingConsumers";
import useEmptyListingRedirect from "../../../../hooks/useEmptyListingRedirect";
import AddCustomerModal from "../../../common/Modals/AddCustomerModal";
import ConfirmationModal from "../../../common/Modals/ConfirmationModal";
import { handleApi } from "../../../../apis";
import { PATCH_UPDATE_TRANSACTION } from "../../../../apis/configuration";
import { useSnackbar } from "../../../../context/SnackbarContext";
import ContactRow from "./ContactRow";
import CompanyRow from "./CompanyRow";
import TransactionRow from "./TransactionRow";
import walletMainStyles from "../WalletMain.module.css";
import styles from "./TransactionListing.module.css";
import AttachContactMessage from "../../../common/AttachContactMessage";

const VIEW_OPTIONS_KEYS = [
  { id: "contact", labelKey: "contact", value: "contact" },
  { id: "transaction", labelKey: "transaction", value: "transaction" },
];

const TransactionListingPage = ({
  txnTypes = [],
  title = null,
  customerPhone = null,
  customerName = null,
  contactDetailRoute = null,
  analyticsEvent = null,
  actionButton = null,
  customers = [],
  billingContactsCacheRef = null,
  emptyRedirectRoute = null,
}) => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const [contactSearchQ, setContactSearchQ] = useState("");
  const [updatePhoneState, setUpdatePhoneState] = useState(null); // { contact, item }
  const [attachState, setAttachState] = useState(null);           // { contact, item }
  const [attachLoading, setAttachLoading] = useState(false);
  const [isResetFilter, setIsResetFilter] = useState(false);
  const [manualContactView, setManualContactView] = useState(false);

  const isContactDetail = Boolean(customerPhone);
  const location = useLocation();

  const {
    searchQ,
    setSearchQ,
    filters,
    setFilters,
    transactions,
    hasNext,
    totalCount,
    isLoading,
    isInitialLoading,
    isError,
    companyOptions,
    typeOptions,
    handleLoadMore,
    handleFilterChange,
    handleRemoveChip,
    resetAndFetch,
    updateTransactionItem,
  } = useTransactionListing({ txnTypes, customerPhone });

  const isTransactionView = filters.view === "transaction";

  // L1 contact view fetches from dedicated billing customers API
  const billingCustomers = useBillingCustomers({
    search: contactSearchQ,
    enabled: !isContactDetail && !isTransactionView,
    cacheRef: billingContactsCacheRef,
  });

  // L2 contact detail view fetches company/consumer list for a specific customer
  const billingConsumers = useBillingConsumers({
    customerPhone: isContactDetail ? customerPhone : null,
    seedData: isContactDetail ? (location.state?.recentTransactions || null) : null,
  });

  // Contact view shows only View + Search; transaction view shows all
  const filtersConfig = useMemo(() => {
    const viewOptions = VIEW_OPTIONS_KEYS.map((opt) => ({
      id: opt.id,
      label: I18n[opt.labelKey],
      value: opt.value,
    }));
    const viewConfig = {
      id: "view",
      label: I18n.view,
      type: "sort",
      options: viewOptions,
    };

    const [searchConfig] = buildFiltersConfig(["keywords"], [], null, null, I18n.search_by_name_or_phone, null, null, null, null, I18n);

    if (!isTransactionView) {
      // L2 contact detail has no search — only the View toggle
      return isContactDetail ? [viewConfig] : [viewConfig, searchConfig];
    }

    const base = buildFiltersConfig(["date", "amount"], [], null, null, I18n.search_by_name_or_phone, null, null, null, null, I18n);
    const result = [
      viewConfig,
      ...base,
      { id: "companies", label: I18n.company, type: "checkbox", options: companyOptions, disabled: !companyOptions.length, preserveCase: true },
      { id: "type", label: I18n.type, type: "checkbox", options: typeOptions, disabled: !typeOptions.length, preserveCase: true },
      searchConfig,
    ];
    return result;
  }, [isTransactionView, isContactDetail, companyOptions, typeOptions, I18n]);

  const activeSearchQ = isTransactionView ? searchQ : contactSearchQ;

  const filterState = useMemo(() => ({
    view: filters.view,
    keywords: activeSearchQ,
    date: { startDate: filters.startDate ?? null, endDate: filters.endDate ?? null },
    amount: {
      min: filters.fromAmount != null && filters.fromAmount !== "" ? String(filters.fromAmount) : "",
      max: filters.toAmount != null && filters.toAmount !== "" ? String(filters.toAmount) : "",
    },
    companies: filters.companies || [],
    type: filters.txnSubTypes || [],
  }), [filters, activeSearchQ]);

  const appliedChips = useMemo(() => {
    const chips = [];
    // View chip: enables the × button on the View filter button when Transaction is selected
    if (isTransactionView) {
      chips.push({ id: "view", label: I18n.view, value: I18n.transaction });
    }
    if ((activeSearchQ || "").trim()) {
      chips.push({ id: "keywords", label: "Search", value: activeSearchQ.trim(), count: 1 });
    }
    if (isTransactionView) {
      if (filters.startDate || filters.endDate) {
        chips.push({
          id: "date",
          label: I18n.date,
          value: `${filters.startDate ? moment(filters.startDate).format("MMM DD") : I18n.start} - ${filters.endDate ? moment(filters.endDate).format("MMM DD") : I18n.end}`,
        });
      }
      if (filters.fromAmount || filters.toAmount) {
        chips.push({
          id: "amount",
          label: "Amount",
          value: `Rs. ${formatNum(filters.fromAmount || 0)} - ${filters.toAmount ? formatNum(filters.toAmount) : "∞"}`,
        });
      }
      if (filters.companies?.length) {
        chips.push({
          id: "companies",
          label: I18n.company,
          value: filters.companies.length > 1 ? `(${filters.companies.length})` : filters.companies[0],
          count: filters.companies.length,
        });
      }
      if (filters.txnSubTypes?.length) {
        chips.push({
          id: "type",
          label: I18n.type,
          value: filters.txnSubTypes.length > 1 ? `(${filters.txnSubTypes.length})` : filters.txnSubTypes[0],
          count: filters.txnSubTypes.length,
        });
      }
    }
    return chips;
  }, [filters, activeSearchQ, isTransactionView, I18n]);

  const isAppliedFilter = appliedChips.filter(c => c.id !== "view").length > 0 || (activeSearchQ || "").trim().length > 0;

  const handleClearAllFilters = () => {
    setSearchQ("");
    setContactSearchQ("");
    setFilters(initialListingFilters);
    resetAndFetch(initialListingFilters, "");
    setIsResetFilter(true);
  };

  const handleContactSelected = useCallback((contact, item) => {
    if (!contact?.phone) {
      showSnackbar({ message: I18n.please_add_a_phone_number_to_continue, severity: "info" });
      setUpdatePhoneState({ contact, item });
      return;
    }
    setAttachState({ contact, item });
  }, [showSnackbar, I18n]);

  const handleAttachConfirm = useCallback(async () => {
    if (!attachState) return;
    const { contact, item } = attachState;
    setAttachLoading(true);
    try {
      const endpoint = {
        ...PATCH_UPDATE_TRANSACTION,
        url: `${PATCH_UPDATE_TRANSACTION.url}${item.id}/`,
      };
      const res = await handleApi(endpoint, {
        customer_name: contact.name || "",
        customer_phone: contact.phone || "",
        category_id: "",
      }, null, navigate);
      if (res?.success !== false) {
        updateTransactionItem(item.reference_id, { customer_name: contact.name || "" });
        setAttachState(null);
        showSnackbar({ message: I18n.contact_linked_successfully, severity: "success" });
      } else {
        showSnackbar({ message: res?.message || I18n.failed_to_link_contact, severity: "error" });
      }
    } catch (err) {
      showSnackbar({ message: err?.response?.data?.message || I18n.failed_to_link_contact, severity: "error" });
    } finally {
      setAttachLoading(false);
    }
  }, [attachState, navigate, updateTransactionItem, showSnackbar, I18n]);

  const handleRowClick = (item) => {
    if (item.id) {
      navigate(`/UCash/TransactionDetail/bill_payment/${item.id}`);
    }
  };

  const handleContactViewDetail = (contact) => {
    if (analyticsEvent) triggerLogEvent(analytics, `${analyticsEvent}_contact_detail`);
    if (contactDetailRoute) {
      navigate(contactDetailRoute, {
        state: {
          customerPhone: contact.customer_phone,
          customerName: contact.display_name,
          recentTransactions: contact.recent_transactions || null,
        },
      });
    }
  };

  const handleViewBills = (consumer) => {
    navigate("/UCash/BillTransactions", {
      state: {
        customerPhone,
        customerName,
        consumerNumber: consumer.consumer_number || consumer.consumer_number_masked,
        companyName: consumer.bill_company || consumer.billing_company,
        billingCompany: consumer.bill_company || consumer.billing_company,
      },
    });
  };

  // Determine which data source, loading state, and pagination to use
  const isContactListView = !isTransactionView && !isContactDetail;
  const isContactDetailConsumerView = isContactDetail && !isTransactionView;

  // True when contact list is empty with no active search — we want to auto-switch to transaction view.
  // manualContactView blocks this when the user has explicitly switched to contact view,
  // giving the fetch time to complete before we decide whether to auto-switch.
  const shouldAutoSwitch =
    !manualContactView &&
    isContactListView &&
    !billingCustomers.isInitialLoading &&
    billingCustomers.contacts.length === 0 &&
    !contactSearchQ;

  // Fire the actual filter change; shouldAutoSwitch is derived synchronously so the spinner
  // renders immediately (no NonIdeal flash), and this effect just keeps filter state in sync
  useEffect(() => {
    if (shouldAutoSwitch) handleFilterChange("view", "transaction");
  }, [shouldAutoSwitch, handleFilterChange]);

  // Clear manualContactView once the fetch triggered by the manual switch has completed
  useEffect(() => {
    if (manualContactView && isContactListView && !billingCustomers.isInitialLoading) {
      setManualContactView(false);
    }
  }, [manualContactView, isContactListView, billingCustomers.isInitialLoading]);

  const currentIsInitialLoading = isContactListView
    ? billingCustomers.isInitialLoading
    : isContactDetailConsumerView
      ? billingConsumers.isInitialLoading
      : isInitialLoading;

  const currentIsLoading = isContactListView
    ? billingCustomers.isLoading
    : isContactDetailConsumerView
      ? billingConsumers.isLoading
      : isLoading;

  const currentHasNext = isContactListView
    ? billingCustomers.hasNext
    : isContactDetailConsumerView
      ? billingConsumers.hasNext
      : hasNext;

  const currentHandleLoadMore = isContactListView
    ? billingCustomers.handleLoadMore
    : isContactDetailConsumerView
      ? billingConsumers.handleLoadMore
      : handleLoadMore;

  const currentListEmpty = isContactListView
    ? billingCustomers.contacts.length === 0
    : isContactDetailConsumerView
      ? billingConsumers.consumers.length === 0
      : transactions.length === 0;

  // Landing on the top-level listing with genuinely no data (not filtered, not a
  // specific contact's history, and not because the fetch itself failed) — send the
  // user straight to the pay flow instead of a dead end. Mirrors the exact condition
  // under which the "No transactions found" empty state below would otherwise render,
  // so it never fires while shouldAutoSwitch/manualContactView are still resolving the
  // contact-list-vs-transaction-view auto-switch.
  const currentIsError = isContactListView ? billingCustomers.isError : isError;
  useEmptyListingRedirect(
    !currentIsInitialLoading && !shouldAutoSwitch && !manualContactView &&
    currentListEmpty && !isAppliedFilter && !isContactDetail && !currentIsError,
    emptyRedirectRoute,
  );

  const pageTitle = isContactDetail && customerName ? customerName : (title || I18n.transactions);

  return (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: pageTitle,
              isBack: true,
              onBack: isContactDetail ? () => navigate(-1) : () => navigate("/UCash"),
              id: "transaction_listing",
              isTitleCase:false
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {actionButton}
            </Box>
          </TitleAndActionBox>

          <InnerWrapper className="innerWrapper newInnerWrapper">
            <AppTableFilters
              filtersConfig={filtersConfig}
              filterState={filterState}
              isResetFilter={isResetFilter}
              setIsResetFilter={setIsResetFilter}
              onFilterChange={(key, value) => {
                if (analyticsEvent) triggerLogEvent(analytics, `${analyticsEvent}_filter_applied`);
                if (String(key).toLowerCase() === "view" && value === "contact" && !isContactDetail) {
                  setManualContactView(true);
                }
                // Contact view search goes to billing customers, not transaction listing
                if (String(key).toLowerCase() === "keywords" && !isTransactionView && !isContactDetail) {
                  setContactSearchQ(value ?? "");
                  return;
                }
                handleFilterChange(key, value);
              }}
              appliedChips={appliedChips}
              onRemoveChip={(chip) => {
                if (analyticsEvent) triggerLogEvent(analytics, `${analyticsEvent}_filter_removed`);
                if (chip.id === "view" && !isContactDetail) setManualContactView(true);
                if (chip.id === "keywords" && !isTransactionView && !isContactDetail) {
                  setContactSearchQ("");
                  return;
                }
                handleRemoveChip(chip);
              }}
            >
              {!isTransactionView && (
                <UBButton
                  id="bill_txn_view_transactions_btn"
                  title={I18n.transactions}
                  iconComponent={<DoubleArrowIcon width={12} height={12} />}
                  className={styles.viewTransactionsBtn}
                  onClick={() => handleFilterChange("view", "transaction")}
                />
              )}
            </AppTableFilters>

            {/* Fix 2: key forces remount of header+list when view changes */}
            <Box key={`${isTransactionView ? "txn" : "contact"}-${isContactDetail ? "detail" : "list"}`}>
              {isTransactionView ? (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.date}</span>
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.company}</span>
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.type}</span>
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.consumer_number}</span>
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.consumer_name}</span>
                  </Box>
                  <Box className="invListTableHeaderCol sixColumn textRight">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.amount}</span>
                  </Box>
                </Box>
              ) : isContactDetail ? (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.company}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.consumer_id}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.consumer_name_caps}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn justifyCenter">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.actions}</span>
                  </Box>
                </Box>
              ) : (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.contact}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.phone_number}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.no_of_bills}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn justifyCenter">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.actions}</span>
                  </Box>
                </Box>
              )}

              <Box className={`${styles.listWrapper} generic-scrollbar`} id="txnListScrollable">
                {currentIsInitialLoading || shouldAutoSwitch || manualContactView ? (
                  <Box className={`${walletMainStyles.transactionListLoadingBox} h-100`}>
                    <CircularProgress size={25} />
                  </Box>
                ) : currentListEmpty ? (
                  <Box className="h-100">
                    {isAppliedFilter ? (
                      <FilterNonIdeal
                        title={I18n.no_transactions_matching_those_filters}
                        subTitle={I18n.try_adjusting_your_filters}
                        subTitleText={I18n.reset_small}
                        subTitleTextOnClick={handleClearAllFilters}
                      />
                    ) : (
                      <FilterNonIdeal
                        title={I18n.no_transactions_found}
                        subTitle={I18n.transactions_will_appear_here_once_available}
                      />
                    )}
                  </Box>
                ) : (
                  <AppPagination
                    key={isTransactionView ? "txn-pager" : "contact-pager"}
                    data={{
                      handleNext: currentHandleLoadMore,
                      pageLoad: currentIsLoading,
                      count: currentHasNext,
                      list: isContactListView
                        ? billingCustomers.contacts
                        : isContactDetailConsumerView
                          ? billingConsumers.consumers
                          : transactions,
                      scrollableTarget: "txnListScrollable",
                    }}
                  >
                    {isTransactionView
                      ? transactions.map((item, idx) => (
                        <TransactionRow
                          key={item.id || idx}
                          item={item}
                          onRowClick={handleRowClick}
                          contacts={customers}
                          onContactSelected={handleContactSelected}
                        />
                      ))
                      : isContactDetail
                        ? billingConsumers.consumers.map((item, idx) => (
                          <CompanyRow
                            key={`${item.bill_company}_${item.consumer_number_masked}_${idx}`}
                            item={item}
                            onViewBills={handleViewBills}
                          />
                        ))
                        : billingCustomers.contacts.map((item, idx) => (
                          <ContactRow
                            key={item.customer_id || `${item.company_name}_${item.consumer_number}_${idx}`}
                            item={item}
                            onViewDetail={handleContactViewDetail}
                          />
                        ))}
                  </AppPagination>
                )}
              </Box>
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>

      <AddCustomerModal
        isOpen={Boolean(updatePhoneState)}
        handleClose={() => setUpdatePhoneState(null)}
        customerToUpdate={updatePhoneState?.contact}
        type="in"
        isTypeField={false}
        isOnlyPhoneList={true}
        onSaveCallback={(updatedContact) => {
          const item = updatePhoneState?.item;
          setUpdatePhoneState(null);
          if (updatedContact && item) setAttachState({ contact: updatedContact, item });
        }}
      />

      <ConfirmationModal
        open={Boolean(attachState)}
        onClose={() => { if (!attachLoading) setAttachState(null); }}
        onConfirm={handleAttachConfirm}
        loading={attachLoading}
        confirmText={I18n.confirm}
        confirmButtonClassName="ubPrimaryButton"
        htmlMessage={
          <AttachContactMessage name={attachState?.contact?.name} />
        }
        id="attach_contact_modal"
      />
    </MainContainer>
  );
};

export default TransactionListingPage;
